import {
  CompanyQuestionnaireDataInterface,
  CreateThoughtPayload,
  CreateUserAssessment,
  QuestionnaireInterface,
  UserResponse,
  assessmentQuestionnaireApiHandler,
  useUserAssessmentHooks,
} from '@api/assessmentQuestionnaireApiHandler';
import { useUserActionsHooks } from '@api/userActionsApiHandler';
import { UpdateUserStatusPayload } from '@api/userStatusApiHandler';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { ScoringType } from '@enums/ScoringTypes';
import { smoothScrollTo } from '@helpers/general.helpers';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { useUpdateUserStatus } from '@hooks/useUserStatus';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ApiResponse } from '@interfaces/index';
import { Box } from '@mui/material';
import { MotionText, showToast } from '@shared/atoms';
import { LoadingBackdrop, SystemButton } from '@shared/molecules';
import { CheckInContent } from '@shared/molecules/CheckInContent/CheckInContent';
import { HelpAndSpeakUp } from '@shared/organisms/EWS/HelpAndSpeakUp/HelpAndSpeakUp';
import { useMutation } from '@tanstack/react-query';
import { ToastMessages } from 'constants/toastMessages';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { RootState } from 'store';
import {
  answerQuestion,
  nextQuestion,
  previousQuestion,
  resetQuestionnaire,
  setCreatedUserAssessment,
  setNavigationStep,
  setQuestionnaireQuestions,
} from 'store/Slices/QuestionnaireSliceV2';
import { AssessmentLoadingScreen } from '../AssessmentLoadingScreen/AssessmentLoadingScreen';
import { FocusAreas } from '../FocusAreas/FocusAreas';
import { LikertMultipleQuestions } from '../LikertMultipleQuestions/LikertMultipleQuestions';
import { QuestionCard } from '../QuestionCard/QuestionCard';
import { QuestionnaireBrakeOff } from '../QuestionnaireBrakeOff/QuestionnaireBrakeOff';
import { Reflection } from '../Reflection/Reflection';
import { TalkToAdviserAreasSelection } from '../TalkToAdviserAreasSelection/TalkToAdviserAreasSelection';
import { DEFAULT_CHECK_IN_VALUE } from './config';
import styles from './styles.module.scss';

export const ContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <main className={styles.container} role="main" aria-label="Assessment content">
    <div className={styles.card}>{children}</div>
  </main>
);

const statusData: UpdateUserStatusPayload = {
  isRegistered: true,
  isJoined: true,
  isEligibleForSpeakUpAlerts: null,
  isOnboardCompleted: true,
  isFirstShortStarCompleted: null,
};

interface SingleQuestionnaireProps {
  companyQuestionnaires: CompanyQuestionnaireDataInterface[] | [];
  questionsData: ApiResponse<QuestionnaireInterface> | undefined;
  questionnairesLoading?: boolean;
  questionnairesError?: Error | null;
  questionsLoading: boolean;
  questionsError?: Error | null;
  isOnboarding?: boolean;
  onComplete?: () => void;
  isLastQuestionnaireInBoarding?: boolean;
  handleUpdateStatus?: () => void;
}

export const mapAnswersToUserResponses = (
  userAnswers: SubmitAssessmentPayload['answers'],
): UserResponse[] =>
  Object.values(userAnswers).map((answer) => ({
    questionId: answer.questionId,
    selectedOptionId: answer.answerId,
    score: answer.answerScore,
  }));

export const SingleQuestionnaire: React.FC<SingleQuestionnaireProps> = (props) => {
  const {
    companyQuestionnaires: questionnaires,
    questionsData,
    questionnairesLoading,
    questionnairesError,
    questionsLoading,
    questionsError,
    isOnboarding,
    onComplete,
    isLastQuestionnaireInBoarding,
    handleUpdateStatus,
  } = props;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { isDesktop } = useWindowDimensions();
  const companyConfig = useAppSelector((state: RootState) => state.companyConfig.data);
  const { showSpeakUp, showInstantHelp } = companyConfig;
  const {
    currentQuestionIndex,
    questionnaire,
    answers,
    chosenQuestionnaire,
    navigationStep,
    thoughtsList,
    scoringType,
    focusAreas,
  } = useAppSelector((state) => state.questionnaireV2 || {});
  const [isLoading, setIsLoading] = useState(true);
  const [questionnaireSubmitting, setQuestionnaireSubmitting] = useState(false);
  const [showEWS, setShowEWS] = useState<boolean>(false);
  const [showWellbeingHub, setShowWellbeingHub] = useState<boolean>(false);
  const queryParams = new URLSearchParams(location.search);
  const queId = queryParams.get('questionnaireId');
  const validatedQuestionnaireId = queId ? Number(queId) : null;
  const indicatorId = queryParams.get('indicatorId');
  const validatedIndicatorId = indicatorId ? Number(indicatorId) : null;
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const [checkInAnnouncement, setCheckInAnnouncement] = useState<string>('');
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
  const [checkInChangeDetected, setCheckInChangeDetected] = useState(false);

  const [isAnswering, setIsAnswering] = useState(false);
  const [localCurrentIndex, setLocalCurrentIndex] = useState(currentQuestionIndex);
  const answerTimeoutRef = useRef<NodeJS.Timeout>();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showExperts, setShowExperts] = useState(false);
  const updateUserActionMutation = useUserActionsHooks.useUpdateUserAction();

  useEffect(() => {
    setLocalCurrentIndex(currentQuestionIndex);
  }, [currentQuestionIndex]);

  const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
    let timeoutId: NodeJS.Timeout;

    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedDispatchAnswer = useCallback(
    debounce((questionId: number, answerId: number, answerScore: number) => {
      dispatch(
        answerQuestion({
          questionId,
          answerId,
          answerScore,
        }),
      );
      setIsAnswering(false);
    }, 400), // 400ms debounce
    [dispatch],
  );

  const useSubmitUserAssessment = () =>
    useMutation({
      mutationFn: ({ questionnaireId, answers: userAnswers }: SubmitAssessmentPayload) => {
        const payload: CreateUserAssessment = {
          questionnaireId,
          userResponses: mapAnswersToUserResponses(userAnswers),
        };

        return assessmentQuestionnaireApiHandler.createUserAssessment(payload);
      },
    });

  const useSubmitUserThoughts = () =>
    useMutation({
      mutationFn: ({ assessmentId, thoughts }: CreateThoughtPayload) => {
        const payload = {
          assessmentId,
          thoughts,
        };

        return assessmentQuestionnaireApiHandler.createThought(payload);
      },
    });

  const {
    mutate: mutateStatus,
    isPending: isPendingStatus,
    isSuccess: isSuccessStatus,
  } = useUpdateUserStatus(false);

  const {
    data: referenceActionsData,
    isLoading: referenceActionsDataLoading,
    error: referenceActionsDataError,
  } = useUserActionsHooks.useGetUserActionsByReference(
    chosenQuestionnaire?.questionnaireV2?.id || null,
  );

  const referenceActionsDataForUpdate = useMemo(() => {
    if (referenceActionsData?.success) {
      return referenceActionsData?.responseObject || [];
    }

    return [];
  }, [referenceActionsData, chosenQuestionnaire]);

  const createCheckInAssessmentMutation = useMutation({
    mutationFn: (payload: CreateUserAssessment) =>
      assessmentQuestionnaireApiHandler.createUserAssessment(payload),
    onSuccess: async (data) => {
      if (data.success) {
        dispatch(setCreatedUserAssessment(data?.responseObject));
        setCheckInAnnouncement('Your check-in was saved — great job!');

        // update actions
        if (referenceActionsDataForUpdate && referenceActionsDataForUpdate.length > 0) {
          Promise.all(
            referenceActionsDataForUpdate.map((dt: { id: number }) =>
              updateUserActionMutation.mutateAsync({
                actionId: dt.id,
                payload: {
                  completed: true,
                  dueDate: null,
                  started: null,
                },
              }),
            ),
          );
        }

        const today = new Date().toISOString().split('T')[0];
        let localStorageData: {
          lastPopupDate?: string;
          popupClosedToday?: boolean;
        } = {};
        const storedData = localStorage.getItem('shortStarCacheHistory');

        if (isOnboarding && onComplete) {
          if (data?.responseObject?.ewsTriggered) {
            if (isLastQuestionnaireInBoarding) {
              await mutateStatus(statusData);
            }

            await dispatch(setNavigationStep('ewsTrigger'));

            if (!storedData) {
              localStorageData = {
                lastPopupDate: today,
                popupClosedToday: true,
              };
              localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
            }
          } else if (!data?.responseObject?.ewsTriggered && isLastQuestionnaireInBoarding) {
            await mutateStatus(statusData, {
              onSuccess: async (dt) => {
                if (dt?.success) {
                  if (handleUpdateStatus) {
                    await handleUpdateStatus();
                  }
                }
              },
            });

            await onComplete();

            if (!storedData) {
              localStorageData = {
                lastPopupDate: today,
                popupClosedToday: true,
              };
              localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
            }

            showToast(ToastMessages.onboarding.success.ONBOARDING_COMPLETED, undefined, 'success');
          } else if (!data?.responseObject?.ewsTriggered && !isLastQuestionnaireInBoarding) {
            await onComplete();

            if (!storedData) {
              localStorageData = {
                lastPopupDate: today,
                popupClosedToday: true,
              };
              localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
            }
          }
        } else {
          setShowEWS(data?.responseObject?.ewsTriggered);
          dispatch(
            setNavigationStep(data?.responseObject?.ewsTriggered ? 'ewsTrigger' : 'focusAreas'),
          );
          dispatch(setCreatedUserAssessment(data?.responseObject));

          if (!storedData) {
            localStorageData = {
              lastPopupDate: today,
              popupClosedToday: true,
            };
            localStorage.setItem('shortStarCacheHistory', JSON.stringify(localStorageData));
          }
        }
      } else {
        showToast(ToastMessages.checkIn.error.CHECK_IN_SAVE_UNSUCCESSFUL, undefined, 'error');
      }
    },
    onError: (error) => {
      showToast(ToastMessages.checkIn.error.CHECK_IN_SAVE_UNSUCCESSFUL, undefined, 'error');
      setQuestionnaireSubmitting(false);
    },
    onSettled: () => {
      setQuestionnaireSubmitting(false);
    },
  });

  const submitAssessmentMutation = useSubmitUserAssessment();
  const submitThoughtMutation = useSubmitUserThoughts();
  const createInsightMutation = useUserAssessmentHooks.useCreateQuestionnaireInsight();

  useEffect(() => {
    if (questionsData) {
      setIsLoading(true);
      const { success, responseObject } = questionsData;
      if (success) {
        dispatch(setQuestionnaireQuestions(responseObject));
        if (!questionsLoading || Boolean(!questionnairesLoading)) {
          setTimeout(() => {
            setIsLoading(false);
          }, 100);
        } else {
          setIsLoading(true);
        }
      } else if (!success && (!questionsLoading || Boolean(!questionnairesLoading))) {
        dispatch(setQuestionnaireQuestions(null));
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      }
    } else {
      dispatch(setQuestionnaireQuestions(null));
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  }, [
    questionsData,
    chosenQuestionnaire?.questionnaireId,
    questionsLoading,
    questionnairesLoading,
  ]);

  useEffect(() => {
    if (
      chosenQuestionnaire &&
      questionsData?.responseObject?.questionnairesV2Questions &&
      questionsData?.responseObject?.questionnairesV2Questions?.length > 0 &&
      chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.CHECK_IN
    ) {
      const initialRatings = questionsData?.responseObject?.questionnairesV2Questions.reduce(
        (acc, que) => {
          const scaleOptions =
            que?.questionnairesV2QuestionResponseScales
              ?.questionnairesV2QuestionResponseScaleOptions;

          // TODO: REMOVED INDEXED DEFAULT VALUE AND ADDED MIN VALUE
          const minValue = Math.min(...scaleOptions.map((opt) => opt.score));

          const totalOptions = scaleOptions?.length ?? 0;
          const halfOptions = totalOptions / 2;
          const centerIndex = Math.round(halfOptions + halfOptions / 2) - 1;
          const defaultScaleValue = scaleOptions?.[centerIndex]?.score ?? 2;

          return { ...acc, [que.id]: DEFAULT_CHECK_IN_VALUE };
        },
        {},
      );

      setRatings(initialRatings);
    }
  }, [chosenQuestionnaire, questionsData]);

  if (questionsLoading || isLoading) {
    return (
      <ContentWrapper>
        <AssessmentLoadingScreen loading={questionsLoading || isLoading} />
      </ContentWrapper>
    );
  }

  // if (contextType === 'reflection' && !chosenQuestionnaire) {
  //   return (
  //     <div className={styles.container}>
  //       <Box className={styles.globalRootCard} sx={{ maxWidth: '600px' }}>
  //         <Reflection
  //           handleStep={() => {
  //             dispatch(resetQuestionnaire());
  //             queryParams.delete('contextType');

  //             history.push({ search: queryParams.toString() });
  //           }}
  //         />
  //       </Box>
  //     </div>
  //   );
  // }

  // TODO: THIS SECTION IS QUESTIONNAIRE SELECTION: COMMENTED FOR FUTURE USAGE
  // if (
  //   (chosenQuestionnaire === null || !chosenQuestionnaire) &&
  //   !isOnboarding &&
  //   !showEWS &&
  //   contextType !== 'reflection'
  // ) {
  //   return (
  //     <ContentWrapper>
  //       <div
  //         className={styles.wrapperQuestionnaire}
  //         role="region"
  //         aria-label="Questionnaire selection"
  //       >
  //         <MotionText
  //           as="h1"
  //           initial={{ opacity: 0, y: 10 }}
  //           animate={{ opacity: 1, y: 0 }}
  //           transition={{ duration: 0.6, ease: 'easeOut' }}
  //           text="Choose a questionnaire to start your assessment"
  //         />

  //         {questionnairesLoading ? (
  //           <LoadingDots
  //             size={isDesktop ? 16 : 25}
  //             marginTop={isDesktop ? '50px' : '60%'}
  //             isHapstarLoading
  //             dotCount={isDesktop ? 5 : 3}
  //             aria-label="Loading questionnaires"
  //           />
  //         ) : (
  //           <div className={styles.list} role="listbox" aria-label="Available questionnaires">
  //             {questionnaires.map((dt, index) => (
  //               <motion.div
  //                 key={index}
  //                 className={styles.questionnaireCard}
  //                 onClick={() => {
  //                   dispatch(setChosenQuestionnaire(dt));
  //                   setIsLoading(true);
  //                 }}
  //                 whileHover={{ scale: 1.05 }}
  //                 whileTap={{ scale: 0.95 }}
  //                 initial={{ opacity: 0, y: 20 }}
  //                 animate={{ opacity: 1, y: 0 }}
  //                 transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.1 }}
  //                 onKeyDown={(e) => {
  //                   if (e.key === KeyConfigurations.ENTER || e.key === KeyConfigurations.SPACE) {
  //                     e.preventDefault();
  //                     dispatch(setChosenQuestionnaire(dt));
  //                     setIsLoading(true);
  //                   }
  //                 }}
  //                 role="option"
  //                 aria-selected="false"
  //                 tabIndex={0}
  //               >
  //                 <MotionText
  //                   as="p"
  //                   initial={{ opacity: 0, y: 10 }}
  //                   animate={{ opacity: 1, y: 0 }}
  //                   transition={{ duration: 0.6, ease: 'easeOut' }}
  //                   text={dt.questionnaireV2.name}
  //                   className={styles.text}
  //                 />
  //               </motion.div>
  //             ))}
  //           </div>
  //         )}

  //         {!questionnairesLoading && questionnaires?.length === 0 && (
  //           <MotionText
  //             as="p"
  //             initial={{ opacity: 0, y: 10 }}
  //             animate={{ opacity: 1, y: 0 }}
  //             transition={{ duration: 0.6, ease: 'easeOut' }}
  //             text="No questionnaires found."
  //             aria-live="polite"
  //           />
  //         )}
  //       </div>
  //     </ContentWrapper>
  //   );
  // }

  if (!questionnaire || questionnairesError || questionsError || !chosenQuestionnaire) {
    if (questionsLoading || questionnairesLoading || isLoading) {
      <ContentWrapper>
        <AssessmentLoadingScreen loading={questionsLoading || isLoading} />
      </ContentWrapper>;
    }

    if (!validatedQuestionnaireId && !isOnboarding) {
      showToast(ToastMessages.questionnaireV2.error.QUESTIONNAIRE_IS_MISSING, undefined, 'error');
    }

    return (
      <ContentWrapper>
        <motion.div
          className={styles.errorContainer}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          role="alert"
          aria-live="assertive"
        >
          <MotionText
            as="h1"
            text="Oops! Something Went Wrong."
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
          <MotionText
            as="h5"
            text="We couldn't load your assessment. Please try again."
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
          <MotionText
            as="p"
            text="If the problem persists, check your internet connection or refresh the page."
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
          <SystemButton
            variant="primarySkyBlue"
            loading={questionnaireSubmitting}
            fullWidthButton
            onClick={() => window.location.reload()}
            disabled={questionnaireSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.75 }}
            aria-label="Try again"
          >
            Try Again
          </SystemButton>
        </motion.div>
      </ContentWrapper>
    );
  }

  const filteredQuestions =
    scoringType === ScoringType.LIKERT_MULTIPLE_QUESTIONS
      ? questionnaire?.questionnairesV2Questions.filter((que, index, arr) => {
          // Always show first question if it's a brakeoff
          if (index === 0 && que.isBrakeOff) return true;

          // Always show last question if it's a brakeoff
          if (index === arr.length - 1 && que.isBrakeOff) return true;

          // For middle questions, exclude brakeoffs
          return !que.isBrakeOff;
        })
      : questionnaire?.questionnairesV2Questions;
  const questions = filteredQuestions;

  const totalQuestions = questions?.length;
  // const question = questions[currentQuestionIndex];
  const question = questions[localCurrentIndex]; // Local question index
  const answersForQuestion =
    (question?.questionnairesV2QuestionResponseScales &&
      question?.questionnairesV2QuestionResponseScales
        ?.questionnairesV2QuestionResponseScaleOptions) ||
    [];
  const isFirstQuestion = localCurrentIndex === 0;
  const isLastQuestion = localCurrentIndex === totalQuestions - 1;

  const questionsWithoutBrakeOff = questions?.filter((q) => !q.isBrakeOff) || [];
  const totalQuestionsWithoutBrakeOff = questionsWithoutBrakeOff.length;

  const currentQuestionIndexWithoutBrakeOff = (() => {
    if (!question || question.isBrakeOff) {
      const previousQuestions = questions.slice(0, localCurrentIndex);
      const previousNonBrakeOffCount = previousQuestions.filter((q) => !q.isBrakeOff).length;

      return Math.max(1, previousNonBrakeOffCount);
    }

    const currentQuestionId = question.id;
    const index = questionsWithoutBrakeOff.findIndex((q) => q.id === currentQuestionId);

    return index >= 0 ? index + 1 : 1;
  })();

  const calculateProgress = () => {
    if (totalQuestionsWithoutBrakeOff === 0) return 0;

    // Count answered non-brake-off questions
    const answeredNonBrakeOffCount = questionsWithoutBrakeOff.filter((q) =>
      answeredQuestions.has(q.id),
    ).length;

    return (answeredNonBrakeOffCount / totalQuestionsWithoutBrakeOff) * 100;
  };

  const progress = calculateProgress();

  const focusAreaEnabledQuestionnaires =
    chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.HOLISTIC ||
    chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.CORE_PILLAR;

  const handleSubmitLikertSingleQuestionAnswers = () => {
    setQuestionnaireSubmitting(true);
    const answersData = Object.values(answers).reduce(
      (
        acc: Record<string, { questionId: number; answerId: number; answerScore: number }>,
        answer: { questionId: number; answerId: number; answerScore: number },
      ) => {
        acc[answer.questionId.toString()] = {
          questionId: answer.questionId,
          answerId: answer.answerId,
          answerScore: answer.answerScore,
        };

        return acc;
      },
      {} as Record<string, { questionId: number; answerId: number; answerScore: number }>,
    );

    const payload: SubmitAssessmentPayload = {
      questionnaireId: chosenQuestionnaire?.questionnaireId ?? 0,
      answers: answersData,
    };

    submitAssessmentMutation.mutate(payload, {
      onSuccess: (response) => {
        if (response?.success) {
          if (thoughtsList?.length > 0) {
            const thoughtPayload = {
              thoughts: thoughtsList,
              assessmentId: response?.responseObject?.id,
            };
            submitThoughtMutation.mutate(thoughtPayload);
          }

          if (
            chosenQuestionnaire?.questionnaireV2?.questionnaireType ===
              QuestionnaireType.CORE_PILLAR ||
            chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.HOLISTIC
          ) {
            createInsightMutation.mutate({
              questionnaireId: response?.responseObject?.questionnaireId,
              assessmentId: response?.responseObject?.id,
              assessmentDate: response?.responseObject?.createdAt,
            });
          }

          // update actions
          if (referenceActionsDataForUpdate && referenceActionsDataForUpdate.length > 0) {
            Promise.all(
              referenceActionsDataForUpdate.map((data: { id: number }) =>
                updateUserActionMutation.mutateAsync({
                  actionId: data.id,
                  payload: {
                    completed: true,
                    dueDate: null,
                    started: null,
                  },
                }),
              ),
            );
          }

          if (isOnboarding) {
            // if (onComplete && response?.responseObject?.ewsTriggered) {
            //   dispatch(setNavigationStep('ewsTrigger'));
            //   dispatch(setCreatedUserAssessment(response?.responseObject));
            //   onComplete();
            // } else if (onComplete && !response?.responseObject?.ewsTriggered) {
            //   onComplete();
            // }
            //TODO: CHECK ONBOARDING FLOW
            if (isLastQuestionnaireInBoarding) {
              mutateStatus(statusData);
            }

            setShowEWS(response?.responseObject?.ewsTriggered);
            dispatch(
              setNavigationStep(
                response?.responseObject?.ewsTriggered
                  ? 'ewsTrigger'
                  : focusAreaEnabledQuestionnaires
                  ? 'focusAreas'
                  : 'reflection',
              ),
            );
            dispatch(setCreatedUserAssessment(response?.responseObject));
          } else {
            setShowEWS(response?.responseObject?.ewsTriggered);
            dispatch(
              setNavigationStep(
                response?.responseObject?.ewsTriggered
                  ? 'ewsTrigger'
                  : focusAreaEnabledQuestionnaires
                  ? 'focusAreas'
                  : 'reflection',
              ),
            );
            dispatch(setCreatedUserAssessment(response?.responseObject));
          }
        } else {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
        }
      },
      onError: (error) => {
        showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
      },
      onSettled: () => {
        setQuestionnaireSubmitting(false);
      },
    });
  };

  const handleSubmitLikertMultipleQuestionAnswers = () => {
    setQuestionnaireSubmitting(true);
    const answersData = Object.values(answers).reduce(
      (
        acc: Record<string, { questionId: number; answerId: number; answerScore: number }>,
        answer: { questionId: number; answerId: number; answerScore: number },
      ) => {
        acc[answer.questionId.toString()] = {
          questionId: answer.questionId,
          answerId: answer.answerId,
          answerScore: answer.answerScore,
        };

        return acc;
      },
      {} as Record<string, { questionId: number; answerId: number; answerScore: number }>,
    );

    const payload: SubmitAssessmentPayload = {
      questionnaireId: chosenQuestionnaire?.questionnaireId ?? 0,
      answers: answersData,
    };

    submitAssessmentMutation.mutate(payload, {
      onSuccess: (response) => {
        if (response?.success) {
          if (thoughtsList?.length > 0) {
            const thoughtPayload = {
              thoughts: thoughtsList,
              assessmentId: response?.responseObject?.id,
            };
            submitThoughtMutation.mutate(thoughtPayload);
          }

          if (
            chosenQuestionnaire?.questionnaireV2?.questionnaireType ===
              QuestionnaireType.CORE_PILLAR ||
            chosenQuestionnaire?.questionnaireV2?.questionnaireType === QuestionnaireType.HOLISTIC
          ) {
            createInsightMutation.mutate({
              questionnaireId: response?.responseObject?.questionnaireId,
              assessmentId: response?.responseObject?.id,
              assessmentDate: response?.responseObject?.createdAt,
            });
          }

          // update actions
          if (referenceActionsDataForUpdate && referenceActionsDataForUpdate.length > 0) {
            Promise.all(
              referenceActionsDataForUpdate.map((data: { id: number }) =>
                updateUserActionMutation.mutateAsync({
                  actionId: data.id,
                  payload: {
                    completed: true,
                    dueDate: null,
                    started: null,
                  },
                }),
              ),
            );
          }

          if (isOnboarding) {
            if (onComplete) {
              // onComplete();
              //TODO: CHECK ONBOARDING FLOW

              if (isLastQuestionnaireInBoarding) {
                mutateStatus(statusData);
              }

              setShowEWS(response?.responseObject?.ewsTriggered);
              dispatch(
                setNavigationStep(
                  response?.responseObject?.ewsTriggered
                    ? 'ewsTrigger'
                    : focusAreaEnabledQuestionnaires
                    ? 'focusAreas'
                    : 'reflection',
                ),
              );
              dispatch(setCreatedUserAssessment(response?.responseObject));
            }
          } else {
            setShowEWS(response?.responseObject?.ewsTriggered);
            dispatch(
              setNavigationStep(
                response?.responseObject?.ewsTriggered
                  ? 'ewsTrigger'
                  : focusAreaEnabledQuestionnaires
                  ? 'focusAreas'
                  : 'reflection',
              ),
            );
            dispatch(setCreatedUserAssessment(response?.responseObject));
          }
        } else {
          showToast('Error occurred. Try again', undefined, 'error');
        }
      },
      onError: (error) => {
        showToast('Error occurred. Try again', undefined, 'error');
      },
      onSettled: () => {
        setQuestionnaireSubmitting(false);
      },
    });
  };

  if (question && question?.isBrakeOff && navigationStep === 'scoring') {
    return (
      <ContentWrapper>
        {questionnaireSubmitting && (
          <LoadingBackdrop text="Just a moment, your answers are being saved…" />
        )}
        <QuestionnaireBrakeOff
          icon={question?.icon ? `${DEFAULT_V2_IMAGE_PATH}/${question?.icon}` : null}
          title={question?.questionText}
          htmlDescription={question?.description}
          nextButtonName={question?.buttonText || 'Continue'}
          onNext={() => {
            dispatch(nextQuestion());

            if (isLastQuestion) {
              if (scoringType === ScoringType.LIKERT_ONE_QUESTION) {
                handleSubmitLikertSingleQuestionAnswers();
              } else if (scoringType === ScoringType.LIKERT_MULTIPLE_QUESTIONS) {
                handleSubmitLikertMultipleQuestionAnswers();
              }
            }
          }}
          prevButtonEnabled={isLastQuestion}
          onPrevious={() => dispatch(previousQuestion())}
          iconBackgroundColor={questionnaire?.color ? questionnaire?.color : null}
        />
      </ContentWrapper>
    );
  }

  const handleRatingChange = (questionId: number, value: number, questionLabel: string) => {
    setRatings((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    if (value !== 0) {
      setCheckInChangeDetected(true);
    }

    setCheckInAnnouncement(`${questionLabel} value set to ${value}`);
  };

  const getSelectedOptionId = (que: any, score: number): number | null => {
    setQuestionnaireSubmitting(true);
    const options =
      que.questionnairesV2QuestionResponseScales.questionnairesV2QuestionResponseScaleOptions;

    const matchingOption = options.find((option: any) => option.score === score);

    return matchingOption ? matchingOption.id : null;
  };

  const handleSubmitCheckInQuestionnaire = () => {
    const userResponses: UserResponse[] = [];

    Object.entries(ratings).forEach(([questionIdStr, score]) => {
      const questionId = parseInt(questionIdStr, 10);
      const que = questions.find((q) => q.id === questionId);

      if (!que) return;

      const selectedOptionId = getSelectedOptionId(que, score);

      if (selectedOptionId === null) {
        showToast(`Invalid score ${score} for question: ${que.questionText}`, undefined, 'error');

        return;
      }

      userResponses.push({
        questionId,
        selectedOptionId,
        score,
      });
    });

    if (userResponses.length !== questions.length) {
      showToast(ToastMessages.checkIn.error.ANSWER_ALL_QUESTIONS, undefined, 'error');

      return;
    }

    const assessmentPayload: CreateUserAssessment = {
      questionnaireId: chosenQuestionnaire?.questionnaireId ?? 0,
      userResponses,
    };

    createCheckInAssessmentMutation.mutate(assessmentPayload);
  };

  return (
    <>
      {isPendingStatus && <LoadingBackdrop text="Loading..." />}
      {questionnaireSubmitting && (
        <LoadingBackdrop text="Just a moment, your answers are being saved…" />
      )}
      {(() => {
        switch (navigationStep) {
          case 'scoring':
          case 'checkIn':
            if (scoringType === ScoringType.CHECK_IN) {
              return (
                <div className={styles.container}>
                  <Box className={styles.checkInGlobalRootCard} role="main">
                    <CheckInContent
                      questionnairesLoading={questionnairesLoading}
                      questionsLoading={questionsLoading}
                      sortedQuestions={questions}
                      ratings={ratings}
                      onRatingChange={handleRatingChange}
                      onSubmit={handleSubmitCheckInQuestionnaire}
                      onClose={() => null}
                      announcement={checkInAnnouncement}
                      sliderChangeDetected={checkInChangeDetected}
                      isSubmitting={questionnaireSubmitting}
                    />
                  </Box>
                </div>
              );
            }

            if (scoringType === ScoringType.LIKERT_ONE_QUESTION) {
              smoothScrollTo(0, 0);

              return (
                <div className={styles.container}>
                  <div className={styles.singleQuestionnaireTop}>
                    <div className={styles.topLikertSingle}>
                      <MotionText
                        as="h3"
                        text={`${(questionnaire && questionnaire?.name) || ''} Assessment`}
                      />

                      <MotionText
                        as="text"
                        text={(questionnaire && questionnaire?.shortDescription) || ''}
                      />

                      {/* Progress Information */}
                      <div className={styles.progressInfo}>
                        <div className={styles.questionProgress}>
                          Question {currentQuestionIndexWithoutBrakeOff || 1} of{' '}
                          {totalQuestionsWithoutBrakeOff || 0}
                        </div>
                        <div className={styles.completionPercentage}>
                          {Math.round(progress || 0)}% completed
                        </div>
                      </div>
                    </div>

                    {/* <div className={styles.container}> */}
                    <QuestionCard
                      question={{
                        ...question,
                        answers:
                          answersForQuestion &&
                          answersForQuestion.map((answer) => ({
                            id: answer.id,
                            optionText: answer.optionText,
                            score: answer.score,
                          })),
                      }}
                      progress={progress}
                      isFirstQuestion={isFirstQuestion}
                      isLastQuestion={isLastQuestion}
                      onAnswer={async (answerId, answerScore) => {
                        // Prevent multiple rapid clicks
                        if (isAnswering) return;

                        setIsAnswering(true);
                        smoothScrollTo(0, 200);

                        const currentQuestion = questions[localCurrentIndex];

                        if (currentQuestion && !currentQuestion.isBrakeOff) {
                          await setAnsweredQuestions(
                            (prev) => new Set([...prev, currentQuestion.id]),
                          );
                        }

                        // Clear any existing timeout
                        if (answerTimeoutRef.current) {
                          await clearTimeout(answerTimeoutRef.current);
                        }

                        // Update local index immediately for UI responsiveness
                        if (!isLastQuestion) {
                          await setLocalCurrentIndex((prevIndex) => prevIndex + 1);
                        }

                        // Dispatch the Redux action with debouncing
                        await debouncedDispatchAnswer(
                          currentQuestion.id,
                          Number(answerId),
                          Number(answerScore),
                        );

                        // Handle last question submission
                        if (isLastQuestion && !currentQuestion?.isBrakeOff) {
                          answerTimeoutRef.current = setTimeout(() => {
                            handleSubmitLikertSingleQuestionAnswers();
                          }, 300); // Wait for Redux state to update
                        }
                      }}
                      onPrevious={() => {
                        // Prevent action during answer processing
                        if (isAnswering) return;

                        const currentQuestion = questions[localCurrentIndex];
                        if (currentQuestion && !currentQuestion.isBrakeOff) {
                          setAnsweredQuestions((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(currentQuestion.id);

                            return newSet;
                          });
                        }

                        // Update local index immediately
                        if (!isFirstQuestion) {
                          setLocalCurrentIndex((prevIndex) => prevIndex - 1);
                        }

                        // Dispatch Redux action
                        dispatch(previousQuestion());
                      }}
                      shareThoughtEnabled={questionnaire?.isThoughtsEnable}
                      questionnaire={questionnaire}
                      totalQuestionsWithoutBrakeOff={totalQuestionsWithoutBrakeOff}
                      currentQuestionIndexWithoutBrakeOff={currentQuestionIndexWithoutBrakeOff}
                    />
                    {/* </div> */}
                  </div>
                </div>
              );
            }

            if (scoringType === ScoringType.LIKERT_MULTIPLE_QUESTIONS) {
              return (
                <LikertMultipleQuestions
                  questions={questions}
                  questionsCount={4}
                  answerDisplayingType="row"
                  shareThoughtEnabled={questionnaire?.isThoughtsEnable}
                  onComplete={() => {
                    if (isLastQuestion && !question?.isBrakeOff) {
                      handleSubmitLikertMultipleQuestionAnswers();
                    } else {
                      handleSubmitLikertMultipleQuestionAnswers();
                    }
                  }}
                  questionnaire={questionnaire}
                />
              );
            }

            return null;

          case 'ewsTrigger':
            return (
              <div className={styles.container}>
                <Box className={styles.globalRootCard}>
                  <HelpAndSpeakUp
                    speakUpVisible={showSpeakUp}
                    previewInstantHelp={showWellbeingHub}
                    handleInstantPreview={setShowWellbeingHub}
                    setShowExperts={setShowExperts}
                    showExperts={showExperts}
                    handleSkip={() => {
                      if (isOnboarding) {
                        if (
                          chosenQuestionnaire?.questionnaireV2?.questionnaireType ===
                            QuestionnaireType.CHECK_IN &&
                          handleUpdateStatus &&
                          isLastQuestionnaireInBoarding &&
                          onComplete
                        ) {
                          handleUpdateStatus();
                          onComplete();

                          showToast(
                            ToastMessages.onboarding.success.ONBOARDING_COMPLETED,
                            undefined,
                            'success',
                          );
                        } else if (
                          chosenQuestionnaire?.questionnaireV2?.questionnaireType ===
                            QuestionnaireType.CHECK_IN &&
                          onComplete
                        ) {
                          dispatch(setNavigationStep('scoring'));
                          onComplete();
                        } else if (
                          chosenQuestionnaire?.questionnaireV2?.questionnaireType !==
                            QuestionnaireType.CHECK_IN &&
                          !focusAreaEnabledQuestionnaires
                        ) {
                          dispatch(setNavigationStep('reflection'));
                          setShowEWS(false);
                        } else if (
                          chosenQuestionnaire?.questionnaireV2?.questionnaireType !==
                            QuestionnaireType.CHECK_IN &&
                          focusAreaEnabledQuestionnaires
                        ) {
                          dispatch(setNavigationStep('focusAreas'));
                          setShowEWS(false);
                        }
                      } else if (!isOnboarding && !focusAreaEnabledQuestionnaires) {
                        dispatch(setNavigationStep('reflection'));
                        setShowEWS(false);
                      } else if (scoringType !== ScoringType.CHECK_IN) {
                        dispatch(setNavigationStep('focusAreas'));
                        setShowEWS(false);
                      }
                    }}
                  />
                </Box>
              </div>
            );

          case 'focusAreas':
            smoothScrollTo(0, 0);

            return (
              <div className="container">
                <FocusAreas />
              </div>
            );

          case 'talkToAdvisor':
            smoothScrollTo(0, 0);

            return (
              <div className={styles.container}>
                <Box className={styles.globalRootCard}>
                  <TalkToAdviserAreasSelection
                    handleSkip={() => dispatch(setNavigationStep('reflection'))}
                  />
                </Box>
              </div>
            );

          case 'reflection':
            smoothScrollTo(0, 0);

            return (
              <div className={styles.container}>
                <Box className={styles.globalRootCard} sx={{ maxWidth: '600px' }}>
                  <Reflection
                    handleStep={async () => {
                      if (isLastQuestionnaireInBoarding && isOnboarding && onComplete) {
                        if (handleUpdateStatus) {
                          await handleUpdateStatus();
                        }

                        showToast(
                          ToastMessages.onboarding.success.ONBOARDING_COMPLETED,
                          undefined,
                          'success',
                        );
                      }

                      if (onComplete && !isOnboarding) {
                        await onComplete();
                        dispatch(resetQuestionnaire());
                      } else if (onComplete && isOnboarding && !isLastQuestionnaireInBoarding) {
                        await dispatch(resetQuestionnaire());
                        onComplete();
                      } else if (!isOnboarding) {
                        if (validatedIndicatorId) {
                          history.push(`/assessment-scores?indicatorId=${validatedIndicatorId}`);
                          dispatch(resetQuestionnaire());
                        } else {
                          history.push(
                            questionnaire && questionnaire?.id
                              ? `/assessment-scores?questionnaireId=${questionnaire.id}`
                              : '/',
                          );
                          dispatch(resetQuestionnaire());
                        }

                        smoothScrollTo(0, 0);
                      }
                    }}
                  />
                </Box>
              </div>
            );

          default:
            return null;
        }
      })()}
    </>
  );
};

export interface SubmitAssessmentPayload {
  questionnaireId: number;
  answers: Record<
    string,
    {
      questionId: number;
      answerId: number;
      answerScore: number;
    }
  >;
}

SingleQuestionnaire.defaultProps = {
  isOnboarding: false,
  questionnairesError: null,
  questionnairesLoading: false,
  questionsError: null,
  onComplete: undefined,
  isLastQuestionnaireInBoarding: false,
  handleUpdateStatus: undefined,
};
